import React, { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import ReactDOM from "react-dom";
import { useLocation } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import axiosInstance from "../../fetchConfig/axiosConfig";
import { toast } from '../../common/toast';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { STATE } from '../../common/mappings';
import logger from '../../utils/logger';

export const compSearchMuniCode = (muniCode, parcelId) => {
  const fromRun = String(muniCode || '').trim().toUpperCase().slice(0, 3);
  if (/^[A-Z]\d{2}$/.test(fromRun)) return fromRun;
  const fromParcel = String(parcelId || '').trim().toUpperCase().slice(0, 3);
  return /^[A-Z]\d{2}$/.test(fromParcel) ? fromParcel : '';
};

// Migrated from deprecated PlacesService to google.maps.places.Place (new API)
// Returns [isValid, propertyInfo] tuple
const geocodeValidation = async (placeId, sessionToken) => {
  if (!window.google?.maps?.places?.Place) {
    logger.error("Google Maps Places (new) API not loaded.");
    return [false, null];
  }

  try {
    const place = new window.google.maps.places.Place({
      id: placeId,
      requestedLanguage: 'en',
    });

    await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

    if (!place.addressComponents) {
      logger.error('No address components in response');
      return [false, null];
    }

    let countyName = "", stateName = "", muniName = "";
    for (const component of place.addressComponents) {
      const types = component.types;
      if (types.includes('locality')) {
        muniName = component.longText;
      } else if (types.includes('administrative_area_level_2')) {
        countyName = component.longText.replace(/ County$/, '').trim();
      } else if (types.includes('administrative_area_level_1')) {
        stateName = component.longText;
      }
    }

    const propertyInfo = { county: countyName, state: stateName, town: muniName };

    const allowedCounties = [
      "Nassau",
      "Suffolk",
      "Westchester",
      "Delaware",
      "Dutchess",
      "Columbia",
      "Greene",
      "Orange",
      "Putnam",
      "Rockland",
      "Sullivan",
      "Ulster"
    ];
    const allowed = stateName === "New York" && allowedCounties.includes(countyName);
    return [allowed, propertyInfo];
  } catch (err) {
    logger.error('Error in geocodeValidation:', err);
    return [false, null];
  }
};

function CompSearch({ handleClose, compSelectCallback, searchData, placeholderText = "Enter Address, or Parcel ID", clearOnSelect = true, value = "", restrictToState = "", muniCode = "" }) {
  const [searchTerm, setSearchTerm] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debouncedTerm, setDebouncedTerm] = useState(value);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [isPidSearch, setIsPidSearch] = useState(false);
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingResult, setPendingResult] = useState(null);
  const [pendingPropertyInfo, setPendingPropertyInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchSeq = useRef(0);

  const searchProperty = async (searchTerm, isPidSearch, propertyInfo = null) => {
    try {
      const address = searchTerm.split(',')[0];
      const propertyLookupData = isPidSearch ? {
        pidOverride: searchTerm
      } : {
        address: address,
        county: propertyInfo?.county,
        town: propertyInfo?.town,
      };

      const response = await axiosInstance.post('/search_property_v2', {
        ...propertyLookupData,
        isPidSearch
      });

      const results = response.data?.data || [];

      if (isPidSearch && results.length !== 1) {
        toast.error('Could not find an exact match for this Parcel ID');
        return null;
      }

      if (!isPidSearch && results.length > 1) {
        return { multipleMatches: true, results };
      }

      if (!isPidSearch && results.length === 0) {
        toast.error('No matches found for this address');
        return null;
      }

      return {
        address: results[0].address,
        parcel_id: results[0].parcel_id,
        county: results[0].county,
        town: results[0].town,
        zip: results[0].zip
      };
    } catch (error) {
      logger.error('Error in property search:', error);
      return null;
    }
  };

  const handleSearchV2 = async (value) => {
    const requestId = ++searchSeq.current;
    setSearchTerm(value);

    if (!value.trim()) {
      setIsPidSearch(false);
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const pidPattern = /^[A-Za-z]\d+/;
    const isPidFormat = pidPattern.test(value);

    setIsPidSearch(isPidFormat);
    setLoading(true);

    if (isPidFormat) {
      logger.log('isPidFormat');
      const result = await searchProperty(value, true);
      if (requestId !== searchSeq.current) return;
      logger.log('result', result);
      if (result) {
        setSuggestions([{
          suggestion: result.address + ', ' + result.town + ", " + STATE + " " + result.zip,
          index: 0,
          parcel_id: result.parcel_id
        }]);
      } else {
        toast.error('Error looking up parcel ID');
      }
      setLoading(false);
      return;
    }

    const limitToNy = restrictToState === 'NY';
    const dbPromise = muniCode && value.trim().length >= 3
      ? axiosInstance.post('/search_muni_sales', { address: value.trim(), muniCode })
          .then((response) => response.data?.data || [])
          .catch((error) => {
            logger.error('Error in municipality sales search:', error);
            return [];
          })
      : Promise.resolve([]);

    const googlePromise = (async () => {
      if (!window.google?.maps?.places?.AutocompleteSuggestion) {
        if (!muniCode) {
          toast.error('Google Maps API is not loaded or does not support AutocompleteSuggestion. Please try again later.');
        } else {
          logger.error('Google Maps API is not loaded or does not support AutocompleteSuggestion.');
        }
        return [];
      }
      const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      const request = {
        input: value,
        sessionToken,
        includedRegionCodes: ['US'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
        ...(limitToNy ? { language: 'en' } : {}),
      };

      const { suggestions: autoSuggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      return (autoSuggestions || []).flatMap((sugg, index) => {
        const placePred = sugg.placePrediction;
        if (!placePred) return [];

        let mainText = placePred.text?.text || '';
        let secondary = placePred.secondaryText?.text || '';
        if (!secondary && Array.isArray(placePred.terms)) {
          const extraTerms = placePred.terms.slice(1).map((term) => term.value);
          if (extraTerms.length) secondary = extraTerms.join(', ');
        }
        const description = secondary && mainText && !mainText.includes(secondary)
          ? `${mainText}, ${secondary}`
          : (mainText || secondary);
        const matchText = `${description} ${placePred.text?.text || ''} ${secondary}`;
        const inNewYork = /,\s*NY(?:\s*,|\s*$)/i.test(matchText) || /,\s*New York(?:\s*,|\s*$)/i.test(matchText);
        if (limitToNy && !inNewYork) return [];
        return [{
          suggestion: description,
          place_id: placePred.placeId,
          sessionToken,
          rawSuggestion: sugg,
          index,
        }];
      });
    })().catch((err) => {
      logger.error('AutocompleteSuggestion.fetchAutocompleteSuggestions error:', err);
      return [];
    });

    try {
      const [dbRows, googleRows] = await Promise.all([dbPromise, googlePromise]);
      if (requestId !== searchSeq.current) return;

      const dbSuggestions = dbRows.filter((row) => row.parcel_id).map((row, index) => ({
        suggestion: [row.address, row.town, `${STATE} ${row.zip || ''}`.trim()].filter(Boolean).join(', '),
        parcel_id: row.parcel_id,
        fromDatabase: true,
        index,
      }));
      const dbAddresses = new Set(dbRows.map((row) => String(row.address || '').trim().toLowerCase()));
      const googleSuggestions = googleRows.filter((row) => {
        const street = String(row.suggestion || '').split(',')[0].trim().toLowerCase();
        return street && !dbAddresses.has(street);
      });
      setSuggestions([...dbSuggestions, ...googleSuggestions]);
    } finally {
      if (requestId === searchSeq.current) setLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestionIndex) => {
    setSuggestions([]);
    setIsSubmitting(true);

    const chosen = suggestions[suggestionIndex];
    const { suggestion, place_id, sessionToken } = chosen;

    const proceedWithPropertyInfo = async (propertyInfo) => {
      const allowedCounties = [
        "Nassau", "Suffolk", "Westchester", "Delaware",
        "Dutchess", "Columbia", "Greene", "Orange",
        "Putnam", "Rockland", "Sullivan", "Ulster"
      ];
      if (!(propertyInfo.state === "New York" && allowedCounties.includes(propertyInfo.county))) {
        setIsSubmitting(false);
        toast.error('We do not currently operate in this location. Please try again with a different address.');
        return;
      }
      try {
        const result = await searchProperty(suggestion, false, propertyInfo);
        if (!result) {
          setIsSubmitting(false);
          return;
        }
        if (result.multipleMatches) {
          logger.log('Multiple matches, showing confirm UI:', result.results);
          setPendingResult(result.results[0]);
          setPendingPropertyInfo(propertyInfo);
          setAnchorEl(inputRef.current);
          setIsSubmitting(false);
          return;
        }
        compSelectCallback({
          suggestion: `${result.address}, ${result.town} ${STATE} ${result.zip}`,
          isGooglePlace: true,
          propertyInfo,
          parcel_id: result.parcel_id
        });
        if (clearOnSelect) {
          setSearchTerm("");
          setDebouncedTerm("");
        } else {
          const fullAddress = `${result.address}, ${result.town} ${STATE} ${result.zip}`;
          setSearchTerm(fullAddress);
          setDebouncedTerm(fullAddress);
        }
        setSuggestions([]);
        setHasSelection(true);
        setIsSubmitting(false);
      } catch (err) {
        logger.error('Error in searchProperty:', err);
        setIsSubmitting(false);
      }
    };

    // Migrated: geocodeValidation is now async and returns [isValid, propertyInfo]
    const [isValid, info] = await geocodeValidation(place_id, sessionToken);
    if (!isValid) {
      setIsSubmitting(false);
      toast.error('We do not currently operate in this location. Please try again with a different address.');
      return;
    }
    await proceedWithPropertyInfo(info);
  };

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length > 0) {
        setDebouncedTerm(searchTerm);
      } else {
        setSuggestions([]);
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Calculate dropdown position dynamically
  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "absolute",
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
      });
    }
  }, [inputRef.current, searchTerm]);

  // Handle initial value and trigger parcel ID lookup if needed
  useEffect(() => {
    if (value && value.trim()) {
      const pidPattern = /^[A-Za-z]\d+/;
      if (pidPattern.test(value)) {
        setLoading(true);
        searchProperty(value, true).then(result => {
          if (result) {
            const fullAddress = `${result.address}, ${result.town} ${STATE} ${result.zip}`;
            setSearchTerm(fullAddress);
            setDebouncedTerm(fullAddress);
            setHasSelection(true);
            compSelectCallback({
              suggestion: fullAddress,
              isGooglePlace: false,
              parcel_id: value
            });
          } else {
            setSearchTerm(value);
            setDebouncedTerm(value);
          }
          setLoading(false);
        }).catch(error => {
          logger.error('Error looking up parcel ID:', error);
          setSearchTerm(value);
          setDebouncedTerm(value);
          setLoading(false);
        });
      } else {
        setSearchTerm(value);
        setDebouncedTerm(value);
      }
    }
  }, [value, compSelectCallback]);

  // Autofocus the input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleConfirmAddress = () => {
    if (!pendingResult) return;

    const fullAddress = `${pendingResult.address}, ${pendingPropertyInfo.town} ${STATE} ${pendingResult.zip}`;
    compSelectCallback({
      suggestion: fullAddress,
      isGooglePlace: true,
      propertyInfo: pendingPropertyInfo,
      parcel_id: pendingResult.parcel_id
    });

    setAnchorEl(null);
    setPendingResult(null);
    setPendingPropertyInfo(null);
    setSuggestions([]);
    if (clearOnSelect) {
      setSearchTerm("");
      setDebouncedTerm("");
    } else {
      setSearchTerm(fullAddress);
      setDebouncedTerm(fullAddress);
    }
  };

  const handleInputChangeV2 = (event) => {
    const value = event.target.value;
    handleSearchV2(value);
  };

  return (
    <div className="relative w-full">
      <TextField
        ref={inputRef}
        autoFocus
        placeholder={placeholderText}
        variant="standard"
        fullWidth
        value={searchTerm}
        onChange={handleInputChangeV2}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {(loading || isSubmitting) && <CircularProgress size={20} />}
              </div>
            </InputAdornment>
          ),
        }}
      />
      {suggestions.length > 0 &&
        ReactDOM.createPortal(
          <ul
            className="bg-white border shadow-md p-1 rounded-md max-h-60 overflow-y-auto"
            style={dropdownStyle}
          >
            {suggestions.map(({ suggestion, parcel_id, fromDatabase }, suggestionIndex) => (
              <React.Fragment key={`${fromDatabase ? 'db' : 'google'}-${suggestionIndex}`}>
                {fromDatabase && suggestionIndex === 0 && (
                  <li className="px-4 pt-2 pb-1 text-[11px] tracking-wide text-gray-400">Quick Search</li>
                )}
                {!fromDatabase && suggestions[suggestionIndex - 1]?.fromDatabase && (
                  <li className="px-4 pt-2 pb-1 text-[11px] tracking-wide text-gray-400">All results</li>
                )}
                <li
                  className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                  onClick={async () => {
                    setSuggestions([]);
                    setIsSubmitting(true);

                    if (fromDatabase || isPidSearch) {
                      compSelectCallback({
                        suggestion,
                        isGooglePlace: false,
                        parcel_id,
                      });

                      if (clearOnSelect) {
                        setSearchTerm("");
                        setDebouncedTerm("");
                      } else {
                        setSearchTerm(suggestion);
                        setDebouncedTerm(suggestion);
                      }
                      setSuggestions([]);
                      setIsSubmitting(false);
                    } else {
                      handleSuggestionClick(suggestionIndex);
                    }
                  }}
                >
                  {suggestion}
                </li>
              </React.Fragment>
            ))}
          </ul>,
          document.body
        )}
      {debouncedTerm && suggestions.length === 0 && !isPidSearch && !isSubmitting && !loading && !pendingResult && isFocused && !hasSelection &&
        ReactDOM.createPortal(
          <ul className="bg-white border shadow-md p-1 rounded-md" style={dropdownStyle}>
            <li className="px-4 py-2 text-center">No matching addresses</li>
          </ul>,
          document.body
        )}
      {debouncedTerm && suggestions.length === 0 && isPidSearch && isFocused && !hasSelection &&
        ReactDOM.createPortal(
          <ul className="bg-white border shadow-md p-1 rounded-md" style={dropdownStyle}>
            <div className="flex justify-center items-center hover:bg-gray-200 cursor-pointer" onClick={() => {
              compSelectCallback({
                suggestion: debouncedTerm,
                isGooglePlace: false,
                parcel_id: debouncedTerm
              });
              if (clearOnSelect) {
                setSearchTerm("");
                setDebouncedTerm("");
              } else {
                setSuggestions([]);
              }
            }}>
              <Add></Add>
              <li className="px-4 py-2 text-center">Add PID</li>
            </div>
          </ul>,
          document.body
        )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setPendingResult(null);
          setPendingPropertyInfo(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: '400px',
            p: 2,
            boxShadow: 3
          }
        }}
      >
        <div className="p-4">
          <Typography variant="h6" className="mb-2">
            Confirm Address
          </Typography>
          <Typography variant="body1" className="mb-4">
            The address you entered did not exactly match our records. Is this the correct address?
          </Typography>
          <Typography variant="body1" className="mb-4 font-semibold">
            {pendingResult?.address && pendingPropertyInfo?.town ?
              `${pendingResult.address}, ${pendingPropertyInfo.town} ${STATE} ${pendingResult.zip || ''}`
              : pendingResult?.address || ''
            }
          </Typography>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setPendingResult(null);
                setPendingPropertyInfo(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddress}
            >
              Yes, Use This Address
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

export default CompSearch;